/**
 * Cor de destaque da loja pública (tenant.theme_json.storeAccent).
 * Mantém o layout; só troca tons de acento (antes fixos em emerald).
 */

export const STORE_ACCENT_KEYS = ['green', 'blue', 'orange', 'yellow', 'red', 'purple'];

export const STORE_ACCENT_LABELS = {
  green: 'Verde',
  blue: 'Azul',
  orange: 'Laranja',
  yellow: 'Amarelo',
  red: 'Vermelho',
  purple: 'Roxo',
};

/** Cor do quadradinho na lista de opções (editar empresa). */
export const STORE_ACCENT_SWATCHES = {
  green: '#059669',
  blue: '#2563eb',
  orange: '#ea580c',
  yellow: '#ffe600',
  red: '#dc2626',
  purple: '#7c3aed',
};

/** @param {unknown} themeJson */
export function parseStoreAccent(themeJson) {
  let obj = themeJson;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return 'green';
    }
  }
  if (!obj || typeof obj !== 'object') return 'green';
  const raw = obj.storeAccent ?? obj.store_accent ?? obj.accent;
  const s = typeof raw === 'string' ? raw.toLowerCase().trim() : '';
  return STORE_ACCENT_KEYS.includes(s) ? s : 'green';
}

/**
 * Variáveis CSS para a raiz da loja (Tailwind arbitrary: var(--st-*)).
 * @param {string} key
 * @returns {Record<string, string>}
 */
export function storeAccentCssVariables(key) {
  const k = STORE_ACCENT_KEYS.includes(key) ? key : 'green';
  return { ...PRESETS[k] };
}

const PRESETS = {
  green: {
    '--st-200': '#a7f3d0',
    '--st-400': '#34d399',
    '--st-500': '#10b981',
    '--st-600': '#059669',
    '--st-700': '#047857',
    '--st-900': '#064e3b',
    '--st-rgb': '16 185 129',
    '--st-drawer': '2 44 34',
    '--st-bd-card': 'rgb(167 243 208 / 0.82)',
    '--st-bd-card-d': 'rgb(6 78 59 / 0.48)',
    '--st-sh-search': '0 12px 40px -14px rgb(16 185 129 / 0.35)',
    '--st-sh-search-d': '0 14px 48px -12px rgb(16 185 129 / 0.22)',
    '--st-sh-sort': '0 4px 16px -4px rgb(16 185 129 / 0.45)',
    '--st-sh-atc': '0 6px 20px -4px rgb(16 185 129 / 0.48)',
    '--st-sh-atc-h': '0 8px 24px -4px rgb(16 185 129 / 0.45)',
  },
  blue: {
    '--st-200': '#bfdbfe',
    '--st-400': '#60a5fa',
    '--st-500': '#3b82f6',
    '--st-600': '#2563eb',
    '--st-700': '#1d4ed8',
    '--st-900': '#1e3a8a',
    '--st-rgb': '37 99 235',
    '--st-drawer': '23 37 84',
    '--st-bd-card': 'rgb(191 219 254 / 0.88)',
    '--st-bd-card-d': 'rgb(30 58 138 / 0.45)',
    '--st-sh-search': '0 12px 40px -14px rgb(37 99 235 / 0.38)',
    '--st-sh-search-d': '0 14px 48px -12px rgb(37 99 235 / 0.26)',
    '--st-sh-sort': '0 4px 16px -4px rgb(37 99 235 / 0.48)',
    '--st-sh-atc': '0 6px 20px -4px rgb(37 99 235 / 0.5)',
    '--st-sh-atc-h': '0 8px 24px -4px rgb(37 99 235 / 0.46)',
  },
  orange: {
    '--st-200': '#fed7aa',
    '--st-400': '#fb923c',
    '--st-500': '#f97316',
    '--st-600': '#ea580c',
    '--st-700': '#c2410c',
    '--st-900': '#7c2d12',
    '--st-rgb': '234 88 12',
    '--st-drawer': '67 20 7',
    '--st-bd-card': 'rgb(254 215 170 / 0.88)',
    '--st-bd-card-d': 'rgb(124 45 18 / 0.45)',
    '--st-sh-search': '0 12px 40px -14px rgb(249 115 22 / 0.4)',
    '--st-sh-search-d': '0 14px 48px -12px rgb(249 115 22 / 0.28)',
    '--st-sh-sort': '0 4px 16px -4px rgb(234 88 12 / 0.5)',
    '--st-sh-atc': '0 6px 20px -4px rgb(234 88 12 / 0.52)',
    '--st-sh-atc-h': '0 8px 24px -4px rgb(234 88 12 / 0.48)',
  },
  /** Amarelo próximo ao #FFE600 do Mercado Livre; 600/700 um pouco mais profundos para contraste com texto branco. */
  yellow: {
    '--st-200': '#fff9b8',
    '--st-400': '#ffec3d',
    '--st-500': '#ffe600',
    '--st-600': '#e6c200',
    '--st-700': '#c4a000',
    '--st-900': '#5c4a06',
    '--st-rgb': '230 194 0',
    '--st-drawer': '62 52 4',
    '--st-bd-card': 'rgb(255 241 150 / 0.92)',
    '--st-bd-card-d': 'rgb(92 74 6 / 0.5)',
    '--st-sh-search': '0 12px 40px -14px rgb(255 230 0 / 0.45)',
    '--st-sh-search-d': '0 14px 48px -12px rgb(255 220 0 / 0.32)',
    '--st-sh-sort': '0 4px 16px -4px rgb(230 194 0 / 0.52)',
    '--st-sh-atc': '0 6px 20px -4px rgb(230 194 0 / 0.55)',
    '--st-sh-atc-h': '0 8px 24px -4px rgb(196 160 0 / 0.5)',
  },
  red: {
    '--st-200': '#fecaca',
    '--st-400': '#f87171',
    '--st-500': '#ef4444',
    '--st-600': '#dc2626',
    '--st-700': '#b91c1c',
    '--st-900': '#7f1d1d',
    '--st-rgb': '220 38 38',
    '--st-drawer': '55 9 9',
    '--st-bd-card': 'rgb(254 202 202 / 0.88)',
    '--st-bd-card-d': 'rgb(127 29 29 / 0.48)',
    '--st-sh-search': '0 12px 40px -14px rgb(239 68 68 / 0.4)',
    '--st-sh-search-d': '0 14px 48px -12px rgb(239 68 68 / 0.28)',
    '--st-sh-sort': '0 4px 16px -4px rgb(220 38 38 / 0.5)',
    '--st-sh-atc': '0 6px 20px -4px rgb(220 38 38 / 0.52)',
    '--st-sh-atc-h': '0 8px 24px -4px rgb(220 38 38 / 0.48)',
  },
  purple: {
    '--st-200': '#ddd6fe',
    '--st-400': '#a78bfa',
    '--st-500': '#8b5cf6',
    '--st-600': '#7c3aed',
    '--st-700': '#6d28d9',
    '--st-900': '#4c1d95',
    '--st-rgb': '124 58 237',
    '--st-drawer': '46 16 101',
    '--st-bd-card': 'rgb(221 214 254 / 0.88)',
    '--st-bd-card-d': 'rgb(76 29 149 / 0.45)',
    '--st-sh-search': '0 12px 40px -14px rgb(139 92 246 / 0.4)',
    '--st-sh-search-d': '0 14px 48px -12px rgb(139 92 246 / 0.28)',
    '--st-sh-sort': '0 4px 16px -4px rgb(124 58 237 / 0.5)',
    '--st-sh-atc': '0 6px 20px -4px rgb(124 58 237 / 0.52)',
    '--st-sh-atc-h': '0 8px 24px -4px rgb(124 58 237 / 0.48)',
  },
};
